import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { fetchPatients } from '../../services/admin/patient.service';
import { useDebounce } from '../../hooks/useDebounce';
import type { UserData } from '../../dtos/admin/user.dto';

export interface PickerOption {
  id: string;
  label: string;
  detail?: string;
  /** Muted warning shown in the list (e.g. on hold, license expired) */
  flag?: string;
}

interface ComboProps {
  label: string;
  value: PickerOption | null;
  options: PickerOption[];
  onQuery?: (q: string) => void;
  onChange: (option: PickerOption | null) => void;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  /** Extra first option, e.g. "Leave unstaffed" */
  emptyOption?: PickerOption;
  id: string;
}

/** Type-ahead select with 44 px rows and keyboard support (replaces the click-twice dropdowns). */
export const Combo: React.FC<ComboProps> = ({ label, value, options, onQuery, onChange, placeholder, loading, disabled, emptyOption, id }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = onQuery || !q ? options : options.filter((o) => `${o.label} ${o.detail || ''}`.toLowerCase().includes(q));
    return [...(emptyOption ? [emptyOption] : []), ...filtered].slice(0, 50);
  }, [options, query, onQuery, emptyOption]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (o: PickerOption) => {
    onChange(emptyOption && o.id === emptyOption.id ? null : o);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={wrapRef} className="relative">
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
      {value && !open ? (
        <div className="flex min-h-[44px] items-center justify-between rounded-md border border-gray-300 bg-white px-3">
          <button type="button" disabled={disabled} className="min-w-0 flex-1 truncate text-left text-base md:text-sm" onClick={() => setOpen(true)}>
            {value.label}{value.detail ? <span className="text-gray-500"> · {value.detail}</span> : null}
          </button>
          {!disabled && (
            <button type="button" aria-label={`Clear ${label}`} className="flex h-9 w-9 items-center justify-center text-gray-400 hover:text-gray-700" onClick={() => onChange(null)}>
              <X size={16} />
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            id={id}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls={`${id}-list`}
            autoComplete="off"
            disabled={disabled}
            value={query}
            placeholder={placeholder}
            className="min-h-[44px] w-full rounded-md border border-gray-300 pl-9 pr-3 text-base focus:outline-none focus:ring-2 focus:ring-slate-500 md:text-sm"
            onFocus={() => setOpen(true)}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); onQuery?.(e.target.value); }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, shown.length - 1)); }
              if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
              if (e.key === 'Enter' && shown[active]) { e.preventDefault(); pick(shown[active]); }
              if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
            }}
          />
        </div>
      )}
      {open && (
        <ul id={`${id}-list`} role="listbox" className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          {loading && <li className="px-3 py-2 text-sm text-gray-500">Searching…</li>}
          {!loading && shown.length === 0 && <li className="px-3 py-2 text-sm text-gray-500">No matches</li>}
          {shown.map((o, i) => (
            <li
              key={o.id}
              role="option"
              aria-selected={i === active}
              className={`flex min-h-[44px] cursor-pointer items-center justify-between gap-2 px-3 text-sm ${i === active ? 'bg-slate-100' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); pick(o); }}
              onMouseEnter={() => setActive(i)}
            >
              <span className="min-w-0 truncate">{o.label}{o.detail ? <span className="text-gray-500"> · {o.detail}</span> : null}</span>
              {o.flag && <span className="shrink-0 text-xs font-medium text-amber-700">{o.flag}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const PATIENT_FLAGS: Record<string, string> = { on_hold: 'On hold', pending_admission: 'Pending admission', inactive: 'Discharged' };

/** Server-side patient search (the old picker loaded only 100 and matched by name). */
export const PatientPicker: React.FC<{ value: PickerOption | null; onChange: (o: PickerOption | null) => void; disabled?: boolean }> = ({ value, onChange, disabled }) => {
  const [query, setQuery] = useState('');
  const debounced = useDebounce(query, 300);
  const [options, setOptions] = useState<PickerOption[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (disabled) return undefined; // locked (patient chart, edit): no search needed
    let live = true;
    setLoading(true);
    fetchPatients(1, 25, debounced || undefined)
      .then(({ patients }) => {
        if (!live) return;
        setOptions(patients
          .filter((p) => String(p.status).toLowerCase() !== 'inactive')
          .map((p) => ({ id: p.id, label: `${p.firstName} ${p.lastName}`.trim(), detail: p.mrn || undefined, flag: PATIENT_FLAGS[String(p.status).toLowerCase()] })));
      })
      .catch(() => live && setOptions([]))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [debounced, disabled]);
  return <Combo id="shift-patient" label="Patient" value={value} options={options} onQuery={setQuery} onChange={onChange} placeholder="Search by name or MRN" loading={loading} disabled={disabled} />;
};

export const UNSTAFFED_OPTION: PickerOption = { id: '__unstaffed__', label: 'Leave unstaffed (open shift)' };

export const clinicianOption = (u: UserData, today?: string): PickerOption => ({
  id: u.id,
  label: `${u.firstName} ${u.lastName}`.trim(),
  detail: u.providerType || undefined,
  flag: u.licenseExpiry && today && u.licenseExpiry < today ? 'License expired' : undefined,
});

export const ClinicianPicker: React.FC<{
  value: PickerOption | null;
  clinicians: UserData[];
  onChange: (o: PickerOption | null) => void;
  today: string;
  allowUnstaffed?: boolean;
  label?: string;
}> = ({ value, clinicians, onChange, today, allowUnstaffed = true, label = 'Clinician' }) => {
  const options = useMemo(() => clinicians.map((c) => clinicianOption(c, today)), [clinicians, today]);
  return (
    <Combo
      id="shift-clinician"
      label={label}
      value={value}
      options={options}
      onChange={onChange}
      placeholder="Search clinicians"
      emptyOption={allowUnstaffed ? UNSTAFFED_OPTION : undefined}
    />
  );
};
